'use client';

import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../utils/cn";

/**
 * Tabs — Radix Tabs primitive.
 *
 * Variants:
 *   default    — underline under the active trigger
 *   pill       — rounded chip highlight
 *   segmented  — grouped segmented control
 *
 * Sizes: L | M | S (matches Button altitude scale).
 * Orientation: horizontal (default) | vertical.
 */

const listVariants = cva(
  "inline-flex items-center gap-space-4 text-ink-muted",
  {
    variants: {
      variant: {
        default: "border-b border-chrome-border w-full",
        pill: "bg-chrome-panel p-space-4 rounded-card",
        segmented:
          "bg-chrome-panel p-space-4 rounded-card border border-chrome-border",
      },
      orientation: {
        horizontal: "flex-row",
        vertical: "flex-col items-stretch border-b-0",
      },
    },
    defaultVariants: { variant: "default", orientation: "horizontal" },
  },
);

const triggerVariants = cva(
  [
    "inline-flex items-center justify-center whitespace-nowrap font-sans font-medium",
    "outline-none transition-colors duration-micro ease-standard",
    "focus-visible:ring-2 focus-visible:ring-accent-violet",
    "disabled:pointer-events-none disabled:opacity-50",
  ].join(" "),
  {
    variants: {
      variant: {
        default: [
          "px-space-16 py-space-8 border-b-2 border-transparent",
          "hover:text-ink-primary",
          "data-[state=active]:text-ink-primary data-[state=active]:border-accent-violet",
        ].join(" "),
        pill: [
          "px-space-12 py-space-4 rounded-chip",
          "hover:text-ink-primary",
          "data-[state=active]:bg-chrome-surface data-[state=active]:text-ink-primary data-[state=active]:shadow-sm",
        ].join(" "),
        segmented: [
          "px-space-12 py-space-4 rounded-chip flex-1",
          "hover:text-ink-primary",
          "data-[state=active]:bg-chrome-surface data-[state=active]:text-ink-primary data-[state=active]:shadow-sm",
        ].join(" "),
      },
      size: {
        L: "text-body-16 h-[40px]",
        M: "text-body-14 h-[36px]",
        S: "text-caption-12 h-[28px]",
      },
    },
    defaultVariants: { variant: "default", size: "M" },
  },
);

export const Tabs = TabsPrimitive.Root;

export interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof listVariants> {}

export const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(function TabsList({ className, variant, orientation, ...props }, ref) {
  return (
    <TabsPrimitive.List
      ref={ref}
      data-slot="tabs-list"
      className={cn(listVariants({ variant, orientation, className }))}
      {...props}
    />
  );
});
TabsList.displayName = "TabsList";

export interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof triggerVariants> {}

export const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(function TabsTrigger({ className, variant, size, ...props }, ref) {
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      data-slot="tabs-trigger"
      className={cn(triggerVariants({ variant, size, className }))}
      {...props}
    />
  );
});
TabsTrigger.displayName = "TabsTrigger";

export const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(function TabsContent({ className, ...props }, ref) {
  return (
    <TabsPrimitive.Content
      ref={ref}
      data-slot="tabs-content"
      className={cn(
        "pt-space-16 outline-none",
        "focus-visible:ring-2 focus-visible:ring-accent-violet rounded-card",
        className,
      )}
      {...props}
    />
  );
});
TabsContent.displayName = "TabsContent";

export { listVariants as tabsListVariants, triggerVariants as tabsTriggerVariants };
